import type { HTTPClient, APIOptions } from '../core/http-client.js';
import { API_ENDPOINTS } from '../core/constants.js';
import type { 
  ModerateConversationsRequest,
  ModerateConversationsResponse
} from '../types/moderation.js';

export class ModerationAPI {
  constructor(private httpClient: HTTPClient) {}

  /**
   * Moderate conversations with specified actions
   * Up to 10 user IDs and up to 2 actions per request
   */
  async moderate(request: ModerateConversationsRequest, options?: APIOptions): Promise<ModerateConversationsResponse> {
    return this.httpClient.request<ModerateConversationsResponse>({
      method: 'POST',
      path: API_ENDPOINTS.MODERATE_CONVERSATIONS,
      body: request,
      accessToken: options?.accessToken,
    });
  }

  /**
   * Block a user from messaging the page
   * Prevents messaging but user can still interact with page content on Facebook
   */
  async blockUser(userIds: string | string[], options?: APIOptions): Promise<ModerateConversationsResponse> {
    const user_ids = Array.isArray(userIds) 
      ? userIds.map(id => ({ id }))
      : [{ id: userIds }];

    return this.moderate({
      user_ids,
      actions: ['block_user'],
    }, options);
  }

  /**
   * Unblock a user to allow messaging again
   */
  async unblockUser(userIds: string | string[], options?: APIOptions): Promise<ModerateConversationsResponse> {
    const user_ids = Array.isArray(userIds)
      ? userIds.map(id => ({ id }))
      : [{ id: userIds }];

    return this.moderate({
      user_ids,
      actions: ['unblock_user'],
    }, options);
  }

  /**
   * Ban a user from both messaging and Facebook interactions
   * More restrictive than blocking - prevents all interactions
   * Note: Cannot ban user who was unbanned in last 48 hours
   */
  async banUser(userIds: string | string[], options?: APIOptions): Promise<ModerateConversationsResponse> {
    const user_ids = Array.isArray(userIds)
      ? userIds.map(id => ({ id }))
      : [{ id: userIds }];

    return this.moderate({
      user_ids,
      actions: ['ban_user'],
    }, options);
  }

  /**
   * Unban a user to restore all interactions
   * Note: Banned user cannot be unblocked, they must be unbanned first
   */
  async unbanUser(userIds: string | string[], options?: APIOptions): Promise<ModerateConversationsResponse> {
    const user_ids = Array.isArray(userIds)
      ? userIds.map(id => ({ id }))
      : [{ id: userIds }];

    return this.moderate({
      user_ids,
      actions: ['unban_user'],
    }, options);
  }

  /**
   * Move conversation to spam folder in Meta Business Suite Inbox
   */
  async moveToSpam(userIds: string | string[], options?: APIOptions): Promise<ModerateConversationsResponse> {
    const user_ids = Array.isArray(userIds)
      ? userIds.map(id => ({ id }))
      : [{ id: userIds }];

    return this.moderate({
      user_ids,
      actions: ['move_to_spam'],
    }, options);
  }

  /**
   * Block user and move to spam (common moderation action)
   */
  async blockAndSpam(userIds: string | string[], options?: APIOptions): Promise<ModerateConversationsResponse> {
    const user_ids = Array.isArray(userIds)
      ? userIds.map(id => ({ id }))
      : [{ id: userIds }];

    return this.moderate({
      user_ids,
      actions: ['block_user', 'move_to_spam'],
    }, options);
  }
}